import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { toastSlideAnimation } from 'src/app/Shared/animations/animations';
import { NotificationBellService } from 'src/app/Shared/notification-bell/notification-bell.service';
import { isAbsoluteNotificationLink } from 'src/app/Shared/notification-bell/notification-bell.interface';
import { NotificationToastState } from './notification-toast.interface';
import { NotificationToastService } from './notification-toast.service';

/**
 * Global in-app push toast -- mounted ONCE in app.component.html (alongside app-loader), not
 * inside NotificationBellComponent (which is instantiated twice -- desktop breadcrumb + mobile
 * navbar -- and would otherwise show two toasts for one push). See NotificationToastService for
 * the show/aggregate/auto-hide/pause-resume logic; this component renders that state plus a
 * countdown progress bar (see startProgressCountdown()) that visually tracks the same auto-hide
 * timer, and pauses that bar (via onMouseEnter/onMouseLeave) exactly when the service's own
 * timer is paused, so the two never drift apart.
 */
@Component({
  selector: 'app-notification-toast',
  templateUrl: './notification-toast.component.html',
  styleUrls: ['./notification-toast.component.css'],
  animations: [toastSlideAnimation]
})
export class NotificationToastComponent implements OnInit, OnDestroy {

  private toastService = inject(NotificationToastService);
  private bellService = inject(NotificationBellService);
  private router = inject(Router);
  private subscription = new Subscription();

  state: NotificationToastState | null = null;

  /** Drives the bottom progress bar's width (100 -> 0) as a plain CSS transition -- see
   *  startProgressCountdown(). Bound via [style.width.%] / [style.transitionDuration.ms]. */
  progressWidthPercent = 100;
  progressTransitionMs = 0;

  /** Skips the Angular entrance/exit animation for anyone with the OS-level "reduce motion"
   *  preference set -- bound to [@.disabled] on the toast element in the template. */
  prefersReducedMotion = typeof window !== 'undefined'
    && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Wall-clock time the CURRENT countdown run started, so onMouseEnter() can work out how much
   *  of the bar's width is left purely from elapsed time -- no DOM reads needed. Re-anchored on
   *  every resume (see the resumed$ subscription below) so the maths stays correct across
   *  repeated pause/resume cycles. */
  private countdownStartedAt: number | null = null;
  /** True while the pointer is over the toast -- checked when a new push aggregates in, so a
   *  still-hovered toast doesn't start counting back down out from under the cursor. */
  private isHovering = false;

  ngOnInit(): void {
    this.subscription.add(this.toastService.state$.subscribe(state => {
      this.state = state;
      if (state) {
        this.startProgressCountdown();
      } else {
        this.countdownStartedAt = null;
        this.isHovering = false;
      }
    }));

    this.subscription.add(this.toastService.resumed$.subscribe(remainingMs => {
      this.countdownStartedAt = Date.now() - (NotificationToastService.VISIBLE_MS - remainingMs);
      this.progressTransitionMs = remainingMs;
      this.progressWidthPercent = 0;
    }));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** "New Notification" / "5 New Notifications" for a live push; a fixed label for the other
   *  two trigger kinds, since their headline text already states the count in words. */
  get eyebrowLabel(): string {
    if (!this.state) { return ''; }
    switch (this.state.kind) {
      case 'away': return 'While You Were Away';
      case 'login': return 'Pending Review';
      default: return this.state.count > 1 ? `${this.state.count} New Notifications` : 'New Notification';
    }
  }

  /** Always the most recently arrived push's title, even while aggregated -- more useful than a
   *  generic "you have new notifications" once several have piled up. For 'away'/'login' this is
   *  the count-message PushNotificationService already built (e.g. "3 notifications arrived"). */
  get headline(): string {
    return this.state?.latestTitle || 'AdvERP';
  }

  /** Single push: its body text. Aggregated push: how many others came in alongside the one
   *  shown above. 'away'/'login': whatever context line PushNotificationService supplied (the
   *  latest unread subject, or a plain call to action). */
  get subtext(): string {
    if (!this.state) { return ''; }
    if (this.state.kind === 'push' && this.state.count > 1) {
      const more = this.state.count - 1;
      return `+${more} more notification${more > 1 ? 's' : ''} -- tap the bell to view all`;
    }
    return this.state.latestBody || '';
  }

  /** Whether the whole card should look/act clickable -- a single live push with a link, an
   *  aggregated push (opens the bell), or any away/login toast (always opens the bell). */
  get isClickable(): boolean {
    if (!this.state) { return false; }
    if (this.state.kind !== 'push') { return true; }
    return this.state.count > 1 || !!this.state.latestUrl;
  }

  /**
   * A single represented push navigates to its link, same convention as the bell row / OS
   * notification click (AttachmentPath / payload.data.url). Everything else that's clickable --
   * an aggregated push, or any away/login toast -- doesn't have one single destination, so it
   * opens the bell dropdown instead (see NotificationBellService.requestOpenDropdown()).
   */
  onToastClick(): void {
    if (!this.state) { return; }
    const singlePushUrl = this.state.kind === 'push' && this.state.count === 1 ? this.state.latestUrl : null;
    this.toastService.dismiss();
    if (singlePushUrl) {
      // A real URL (http://... -- see isAbsoluteNotificationLink's doc comment) needs an actual
      // browser navigation; router.navigateByUrl() would otherwise mangle it into a broken
      // in-app route. New tab so the ERP session/state in this tab isn't lost by leaving the SPA.
      if (isAbsoluteNotificationLink(singlePushUrl)) {
        window.open(singlePushUrl, '_blank', 'noopener');
      } else {
        this.router.navigateByUrl(singlePushUrl);
      }
    } else if (this.isClickable) {
      this.bellService.requestOpenDropdown();
    }
  }

  onClose(event: Event): void {
    event.stopPropagation();
    this.toastService.dismiss();
  }

  /** Hover-to-pause: freezes the auto-hide timer AND the progress bar exactly where they stand,
   *  computed from elapsed time rather than a DOM read. Stays frozen until onMouseLeave(). */
  onMouseEnter(): void {
    this.isHovering = true;
    if (!this.state || this.countdownStartedAt === null) { return; }
    this.toastService.pause();
    const elapsed = Date.now() - this.countdownStartedAt;
    const remainingPercent = Math.max(0, Math.min(100, 100 - (elapsed / NotificationToastService.VISIBLE_MS) * 100));
    this.progressTransitionMs = 0;
    this.progressWidthPercent = remainingPercent;
  }

  /** Resumes for exactly the time that was left -- see the resumed$ subscription in ngOnInit(),
   *  which is what actually restarts the bar's animation once the service confirms how long. */
  onMouseLeave(): void {
    this.isHovering = false;
    this.toastService.resume();
  }

  /**
   * Resets the progress bar to full width with no transition, then -- one frame later, once the
   * browser has painted that reset -- kicks off a linear width transition down to 0 over the
   * same VISIBLE_MS the service auto-hides after. Runs every time state$ emits a non-null value,
   * so a new arrival that gets folded into an already-visible toast (aggregation) restarts the
   * bar from full, in sync with NotificationToastService.show() restarting its own hide timer --
   * UNLESS the toast is still being hovered, in which case it re-freezes at full instead of
   * animating down (see the isHovering check below), so aggregating into a toast the user is
   * currently reading over doesn't start it counting down again under their cursor.
   * The double rAF is the standard trick for forcing a reflow between "jump to 100%" and
   * "animate to 0%" -- without it the browser can coalesce both style writes into one frame and
   * the transition never visibly plays.
   */
  private startProgressCountdown(): void {
    this.progressTransitionMs = 0;
    this.progressWidthPercent = 100;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.state) { return; }
        this.countdownStartedAt = Date.now();
        if (this.isHovering) {
          this.toastService.pause();
          return;
        }
        this.progressTransitionMs = NotificationToastService.VISIBLE_MS;
        this.progressWidthPercent = 0;
      });
    });
  }
}
